import type { EntityStatus } from '@ms/yammer-data-support';
import { getStatus } from '@ms/yammer-data-support';
import createCachedSelector from 're-reselect';

import type { User, UserConnectedLeadersListFields, YammerState } from '../../state/types';
import { userConnectedLeadersListRequestStatusKey } from '../leadersManagement/actions.types';
import { getStatus as getRequestStatus } from '../requestStatuses/selectors';
import { getFulfilledUserConnectedLeaders } from '../userConnectedLeaders/selectors';
import { getFulfilledUsers } from '../users/selectors';

type GetLoadMoreUserConnectedLeadersListStatus = (state: YammerState) => EntityStatus;
export const getLoadMoreUserConnectedLeadersListStatus: GetLoadMoreUserConnectedLeadersListStatus = (state) =>
  getRequestStatus(state, 'UserConnectedLeadersListLoadMore', userConnectedLeadersListRequestStatusKey);

type GetUserConnectedLeadersListStatus = (state: YammerState) => EntityStatus;
export const getUserConnectedLeadersListStatus: GetUserConnectedLeadersListStatus = (state) =>
  getStatus(state.userConnectedLeadersList);

const emptyUserConnectedLeaderIds: User['id'][] = [];
type GetUserConnectedLeadersListIdsFromState = (state: YammerState) => User['id'][];
export const getUserConnectedLeadersListIdsFromState: GetUserConnectedLeadersListIdsFromState = (state: YammerState) =>
  state.userConnectedLeadersList.fulfilled?.userConnectedLeaders ?? emptyUserConnectedLeaderIds;

type GetUserConnectedLeadersList = (state: YammerState) => UserConnectedLeadersListFields[];
/**
 * @deprecated PERFORMANCE This selects a manually-constructed array of UserConnectedLeadersListFields objects,
 * which will return a new instance and force consumers to re-render whenever ANY user entity in the store gets
 * updated. This should be replaced by a `getUserConnectedLeadersIds` selector, and the top-level consuming component
 * can pass each id to a child component which will use `useUserField` or other "field" selectors for each individual
 * field it requires, while managing its own memoization to avoid unnecessary re-renders.
 */
export const getUserConnectedLeadersList: GetUserConnectedLeadersList = createCachedSelector(
  getUserConnectedLeadersListIdsFromState,
  getFulfilledUserConnectedLeaders,
  getFulfilledUsers,
  (userConnectedLeadersListIds, fulfilledUserConnectedLeaders, fulfilledUsers) =>
    userConnectedLeadersListIds.reduce<UserConnectedLeadersListFields[]>((acc, edgeId) => {
      const userConnectedLeader = fulfilledUserConnectedLeaders[edgeId];

      if (!userConnectedLeader) {
        throw new Error('Missing user connected leader data in state for user connected leaders list edge.');
      }

      const { id, totalFollowedUsers, totalFollowers, directAudienceCount } = userConnectedLeader;
      const { name, jobTitle, avatarUrlTemplate } = fulfilledUsers[id];

      acc.push({
        id,
        avatarUrlTemplate,
        name,
        jobTitle,
        totalFollowedUsers,
        totalFollowers,
        directAudienceCount,
      });

      return acc;
    }, [])
)((_state) => 'cache');

type HasNextPageOfUserConnectedLeadersListAvailable = (state: YammerState) => boolean;
export const hasNextPageOfUserConnectedLeadersListAvailable: HasNextPageOfUserConnectedLeadersListAvailable = (state) =>
  state.userConnectedLeadersList.fulfilled?.hasNextPage ?? false;

type GetUserConnectedLeadersOfficeIdList = (state: YammerState) => string[];
export const getUserConnectedLeadersOfficeIdList: GetUserConnectedLeadersOfficeIdList = createCachedSelector(
  getUserConnectedLeadersListIdsFromState,
  getFulfilledUserConnectedLeaders,
  (userConnectedLeadersListIds, fulfilledUserConnectedLeaders) =>
    userConnectedLeadersListIds.reduce<string[]>((acc, edgeId) => {
      const userConnectedLeader = fulfilledUserConnectedLeaders[edgeId];

      if (!userConnectedLeader) {
        throw new Error('Missing user connected leader data in state for user connected leaders list edge.');
      }

      const { officeUserId } = userConnectedLeader;
      if (officeUserId) {
        acc.push(officeUserId);
      }

      return acc;
    }, [])
)((_state) => 'cache');
